import { useState, useEffect } from 'react';
import { saveSiteData } from '@/utils/apiClient';

export const DEFAULT_LOGO = '/c2-logo.png';
const CACHE_KEY = 'yg_logo_url';

export type LogoData =
  | { type: 'url'; value: string }
  | { type: 'uploaded'; url: string; fileId: string };

// In-memory cache
let _logoUrl: string | null = null;

export async function fetchLogoUrl(): Promise<string> {
  if (_logoUrl !== null) return _logoUrl;
  // Önce local cache'e bak (API yüklenene kadar)
  const cached = sessionStorage.getItem(CACHE_KEY);
  if (cached) { _logoUrl = cached; return cached; }
  return DEFAULT_LOGO;
}

export function setLogoUrlCache(url: string): void {
  _logoUrl = url;
  sessionStorage.setItem(CACHE_KEY, url);
  window.dispatchEvent(new CustomEvent('ygLogoChanged', { detail: url }));
}

export async function saveLogoUrl(url: string, fileId?: string): Promise<void> {
  const data: LogoData = fileId
    ? { type: 'uploaded', url, fileId }
    : { type: 'url', value: url };
  await saveSiteData('yg_logo', data);
  setLogoUrlCache(url);
}

export async function resetLogo(): Promise<void> {
  await saveSiteData('yg_logo', null);
  _logoUrl = null;
  sessionStorage.removeItem(CACHE_KEY);
  window.dispatchEvent(new CustomEvent('ygLogoChanged', { detail: DEFAULT_LOGO }));
}

export function initLogoFromCache(siteDataCache: Record<string, unknown>): void {
  const data = siteDataCache['yg_logo'] as LogoData | null | undefined;
  if (!data) return;
  const url = data.type === 'url' ? data.value : data.url;
  if (url) setLogoUrlCache(url);
}

export default function useLogoUrl(): string {
  const [logoUrl, setLogoUrl] = useState<string>(_logoUrl ?? DEFAULT_LOGO);

  useEffect(() => {
    fetchLogoUrl().then((url) => setLogoUrl(url));

    const handler = (e: Event) => {
      const detail = (e as CustomEvent<string>).detail;
      setLogoUrl(detail || DEFAULT_LOGO);
    };
    window.addEventListener('ygLogoChanged', handler);
    return () => window.removeEventListener('ygLogoChanged', handler);
  }, []);

  return logoUrl;
}

// Legacy compat
export function getLogoData(): null { return null; }
export function setLogoData(_data: unknown): void { /* noop */ }
